import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { AnimatePresence, motion } from 'framer-motion';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  LineChart,
  Line,
  CartesianGrid,
  Legend,
} from 'recharts';

import './App.css';

import Modal from './components/Modal';
import RippleButton from './components/RippleButton';
import ToastHost from './components/ToastHost';
import ThemeToggle from './components/ThemeToggle';
import UserMenu from './components/UserMenu';
import CategoryMultiSelect from './components/Filters/CategoryMultiSelect';
import MonthDropdown from './components/Filters/MonthDropdown';
import MobileBottomNav from './components/MobileBottomNav';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

const SIDEBAR_ITEMS = [
  { id: 'dashboard', label: 'Dashboard' },
  { id: 'expenses', label: 'Expenses' },
  { id: 'insights', label: 'Insights' },
];

const CATEGORY_COLORS = ['#6366f1', '#f97316', '#22c55e', '#e11d48', '#06b6d4', '#a855f7', '#facc15', '#64748b'];

function pad2(n) {
  return String(n).padStart(2, '0');
}

function monthValueFromDate(d) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`;
}

function getMonthOptions(count = 7) {
  const now = new Date();
  const options = [];
  for (let i = 0; i < count; i += 1) {
    const d = new Date(now);
    d.setMonth(d.getMonth() - i);
    options.push(monthValueFromDate(d));
  }
  return options;
}

function monthDayKey(dateValue) {
  const d = new Date(dateValue);
  return `${d.getDate()}/${d.getMonth() + 1}`;
}

function createId() {
  return `exp_${Math.random().toString(16).slice(2)}_${Date.now()}`;
}

export default function App() {
  const [activePage, setActivePage] = useState('dashboard');
  const [theme, setTheme] = useState('dark');

  const [token, setToken] = useState(null);
  const [user, setUser] = useState(null);
  const [authMode, setAuthMode] = useState('login');
  const [authForm, setAuthForm] = useState({ name: '', email: '', password: '' });
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState('');

  const [toasts, setToasts] = useState([]);

  const pushToast = (type, title, message) => {
    const id = createId();
    setToasts((prev) => [{ id, type, title, message }, ...prev].slice(0, 4));
    window.setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 2600);
  };

  const [monthFilter, setMonthFilter] = useState(() => monthValueFromDate(new Date()));
  const monthOptions = useMemo(() => getMonthOptions(8), []);

  const [expenses, setExpenses] = useState([]);
  const [summary, setSummary] = useState(null);
  const [expensesLoading, setExpensesLoading] = useState(false);
  const [expensesError, setExpensesError] = useState('');

  // Debounced search
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(searchInput), 220);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  // Multi-select category filter
  const [selectedCategories, setSelectedCategories] = useState([]);

  const availableCategories = useMemo(() => {
    const set = new Set();
    expenses.forEach((e) => set.add(e.category));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [expenses]);

  const [newlyAddedId, setNewlyAddedId] = useState(null);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [addLoading, setAddLoading] = useState(false);

  const [newExpenseForm, setNewExpenseForm] = useState({
    amount: '',
    category: 'Food',
    description: '',
    date: new Date().toISOString().slice(0, 10),
  });

  const [aiText, setAiText] = useState('');
  const [aiLoading, setAiLoading] = useState(false);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
  }, [theme]);

  useEffect(() => {
    const storedToken = localStorage.getItem('ai-expense-token');
    const storedUser = localStorage.getItem('ai-expense-user');
    if (storedToken && storedUser) {
      setToken(storedToken);
      setUser(JSON.parse(storedUser));
    }
  }, []);

  async function handleAuthSubmit(e) {
    e.preventDefault();
    setAuthLoading(true);
    setAuthError('');
    try {
      const url = authMode === 'login' ? `${API_BASE_URL}/api/auth/login` : `${API_BASE_URL}/api/auth/register`;
      const payload =
        authMode === 'login'
          ? { email: authForm.email, password: authForm.password }
          : authForm;
      const res = await axios.post(url, payload);
      setToken(res.data.token);
      setUser(res.data.user);
      localStorage.setItem('ai-expense-token', res.data.token);
      localStorage.setItem('ai-expense-user', JSON.stringify(res.data.user));
      pushToast('success', authMode === 'login' ? 'Logged in' : 'Account created', 'Welcome to Kharcha AI.');
    } catch (err) {
      console.error(err);
      setAuthError(err.response?.data?.message || 'Authentication failed');
    } finally {
      setAuthLoading(false);
    }
  }

  function handleLogout() {
    setToken(null);
    setUser(null);
    setExpenses([]);
    setSummary(null);
    localStorage.removeItem('ai-expense-token');
    localStorage.removeItem('ai-expense-user');
    pushToast('success', 'Logged out', 'Session ended.');
  }

  const fetchExpenses = async () => {
    if (!token) return;
    setExpensesLoading(true);
    setExpensesError('');

    try {
      // Fetch all expenses in one call
      const res = await axios.get(`${API_BASE_URL}/api/expenses`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      // Store all expenses for historical charts
      if (res.data.expenses) {
        window.allExpensesData = res.data.expenses;
      }
      
      // Filter current month expenses
      const currentMonthExpenses = res.data.expenses.filter(expense => {
        const expenseDate = new Date(expense.date);
        const [year, month] = monthFilter.split('-').map(Number);
        return expenseDate.getFullYear() === year && expenseDate.getMonth() + 1 === month;
      });
      
      // Calculate summary for current month
      const total = currentMonthExpenses.reduce((sum, e) => sum + e.amount, 0);
      const byCategory = currentMonthExpenses.reduce((acc, e) => {
        acc[e.category] = (acc[e.category] || 0) + e.amount;
        return acc;
      }, {});

      // Week-over-week comparison
      const now = new Date();
      const startOfThisWeek = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      const startOfLastWeek = new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000);

      const thisWeekExpenses = currentMonthExpenses.filter((e) => new Date(e.date) >= startOfThisWeek);
      const lastWeekExpenses = currentMonthExpenses.filter((e) => {
        const date = new Date(e.date);
        return date >= startOfLastWeek && date < startOfThisWeek;
      });

      const thisWeekTotal = thisWeekExpenses.reduce((sum, e) => sum + e.amount, 0);
      const lastWeekTotal = lastWeekExpenses.reduce((sum, e) => sum + e.amount, 0);

      // Generate insights
      const insights = [];
      if (total > 0) {
        const sortedCategories = Object.entries(byCategory).sort((a, b) => b[1] - a[1]);
        if (sortedCategories.length > 0) {
          const [topCategory, topAmount] = sortedCategories[0];
          const percentage = Math.round((topAmount / total) * 100);
          insights.push(`You spend around ${percentage}% on ${topCategory}.`);
        }
      }

      if (lastWeekTotal > 0) {
        const diff = thisWeekTotal - lastWeekTotal;
        const change = Math.round((Math.abs(diff) / lastWeekTotal) * 100);
        if (diff > 0) {
          insights.push(`Your spending increased by about ${change}% compared to last week.`);
        } else if (diff < 0) {
          insights.push(`Nice! Your spending decreased by about ${change}% compared to last week.`);
        }
      }
      
      setExpenses(currentMonthExpenses);
      setSummary({ total, byCategory, thisWeekTotal, lastWeekTotal, insights });
    } catch (err) {
      console.error(err);
      if (err.response?.status === 401) {
        handleLogout();
      }
      setExpensesError('Failed to load expenses.');
    } finally {
      setExpensesLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchExpenses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthFilter, token]);

  const filteredExpenses = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    const selectedSet = new Set(selectedCategories);
    return expenses.filter((e) => {
      const matchesSearch = !q || e.description?.toLowerCase().includes(q) || e.category?.toLowerCase().includes(q);
      const matchesCategory = selectedSet.size === 0 || selectedSet.has(e.category);
      return matchesSearch && matchesCategory;
    });
  }, [expenses, debouncedSearch, selectedCategories]);

  const pieData = useMemo(() => {
    if (!summary?.byCategory) return [];
    return Object.entries(summary.byCategory).map(([name, value]) => ({ name, value }));
  }, [summary]);

  const currentMonthLabel = useMemo(() => {
    const [year, month] = monthFilter.split('-');
    const date = new Date(Number(year), Number(month) - 1, 1);
    return date.toLocaleString('default', { month: 'long', year: 'numeric' });
  }, [monthFilter]);

  const lineChartData = useMemo(() => {
    const byDate = {};
    filteredExpenses.forEach((e) => {
      if (!e || !e.date) return;
      const key = monthDayKey(e.date);
      byDate[key] = (byDate[key] || 0) + (Number(e.amount) || 0);
    });
    // Keep chart stable and ordered by actual date: we sort by day number then month+1 as fallback.
    const rows = Object.entries(byDate).map(([date, total]) => ({ date, total }));
    return rows;
  }, [filteredExpenses]);

  const weeklyComparisonData = useMemo(() => {
    const allExpenses = window.allExpensesData || expenses;
    if (!allExpenses || !Array.isArray(allExpenses)) return [];
    
    const now = new Date();
    const weeks = [];
    
    for (let i = 3; i >= 0; i--) {
      const weekStart = new Date(now);
      weekStart.setDate(now.getDate() - (i * 7) + (now.getDay() || 7) - 1);
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6);
      
      const weekTotal = allExpenses
        .filter((e) => {
          if (!e || !e.date) return false;
          const date = new Date(e.date);
          return date >= weekStart && date <= weekEnd;
        })
        .reduce((sum, e) => sum + (Number(e.amount) || 0), 0);
      
      weeks.push({
        week: `Week ${4 - i}`,
        amount: weekTotal,
        start: weekStart.toLocaleDateString(),
        end: weekEnd.toLocaleDateString(),
      });
    }
    
    return weeks;
  }, [expenses]);

  const monthlyTrendData = useMemo(() => {
    const allExpenses = window.allExpensesData || expenses;
    if (!allExpenses || !Array.isArray(allExpenses)) return [];
    
    const months = [];
    const now = new Date();
    
    for (let i = 5; i >= 0; i--) {
      const monthDate = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const nextMonth = new Date(now.getFullYear(), now.getMonth() - i + 1, 1);
      
      const monthTotal = allExpenses
        .filter((e) => {
          if (!e || !e.date) return false;
          const date = new Date(e.date);
          return date >= monthDate && date < nextMonth;
        })
        .reduce((sum, e) => sum + (Number(e.amount) || 0), 0);
      
      months.push({
        month: monthDate.toLocaleDateString('default', { month: 'short', year: '2-digit' }),
        amount: monthTotal,
        fullMonth: monthDate.toLocaleDateString('default', { month: 'long', year: 'numeric' }),
      });
    }
    
    return months;
  }, [expenses]);

  const topCategoryBars = useMemo(() => {
    if (!summary?.byCategory) return [];
    const total = summary.total || 0;
    if (total <= 0) return [];
    return Object.entries(summary.byCategory)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 4)
      .map(([category, amount]) => ({
        category,
        amount,
        pct: Math.round((amount / total) * 100),
      }));
  }, [summary]);

  const handleAnalyzeText = async () => {
    if (!aiText.trim()) return;
    setAiLoading(true);
    try {
      const res = await axios.post(
        `${API_BASE_URL}/api/analyze`,
        { text: aiText },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const { amount, category, description } = res.data || {};
      setNewExpenseForm((prev) => ({
        ...prev,
        amount: amount ? String(amount) : prev.amount,
        category: category || prev.category,
        description: description || prev.description,
      }));
      pushToast('success', 'AI parsed', `Category: ${category}`);
    } catch (err) {
      console.error(err);
      pushToast('error', 'AI parse failed', 'Could not parse the text.');
    } finally {
      setAiLoading(false);
    }
  };

  const handleAddExpense = async (e) => {
    e.preventDefault();
    if (!newExpenseForm.amount || Number(newExpenseForm.amount) <= 0) {
      pushToast('error', 'Validation', 'Amount must be greater than 0.');
      return;
    }

    setAddLoading(true);
    try {
      const res = await axios.post(
        `${API_BASE_URL}/api/expenses`,
        {
          amount: Number(newExpenseForm.amount),
          category: newExpenseForm.category,
          description: newExpenseForm.description,
          date: newExpenseForm.date,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Optimistic UI: add then refresh summary later.
      const created = res.data;
      setExpenses((prev) => [created, ...prev]);
      setNewlyAddedId(created._id);
      pushToast('success', 'Added', 'Expense added successfully.');
      setIsAddModalOpen(false);
      setAiText('');
      await fetchExpenses();
    } catch (err) {
      console.error(err);
      pushToast('error', 'Add failed', 'Could not save the expense.');
    } finally {
      setAddLoading(false);
    }
  };

  const handleDeleteExpense = async (id) => {
    try {
      await axios.delete(`${API_BASE_URL}/api/expenses/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      pushToast('success', 'Deleted', 'Expense removed.');
      await fetchExpenses();
    } catch (err) {
      console.error(err);
      pushToast('error', 'Delete failed', 'Could not delete the expense.');
    }
  };

  const dashboardCards = (
    <section className="dashboard-grid">
      {/* Summary Cards */}
      {[
        { label: 'Total खर्च', value: summary?.total || 0, meta: currentMonthLabel, accent: 'card--primary' },
        {
          label: 'This week',
          value: summary?.thisWeekTotal || 0,
          meta: `vs last week ₹${summary?.lastWeekTotal || 0}`,
        },
        { label: 'Categories', value: pieData.length, meta: 'Active this month' },
        { label: 'Avg Daily', value: Math.round((summary?.total || 0) / 30), meta: 'Per day this month' },
      ].map((card, idx) => (
        <motion.div
          key={card.label}
          className={`card ${card.accent || ''}`}
          initial="hidden"
          animate="show"
          variants={{
            hidden: { opacity: 0, y: 14 },
            show: { opacity: 1, y: 0, transition: { delay: 0.08 * idx, duration: 0.35 } },
          }}
          whileHover={{ scale: 1.02 }}
        >
          <div className="card__label">{card.label}</div>
          <div className="card__value">₹{Number(card.value).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</div>
          <div className="card__meta">{card.meta}</div>
        </motion.div>
      ))}

      {/* Monthly Trend Chart */}
      <motion.div 
        className="card card--chart card--full-width" 
        initial="hidden"
        animate="show"
        variants={{ hidden: { opacity: 0, y: 14 }, show: { opacity: 1, y: 0, transition: { delay: 0.4 } } }}
      >
        <div className="card__header">
          <div>
            <h2 className="card__title">6-Month Spending Trend</h2>
            <p className="card__subtitle">Track your spending patterns over time</p>
          </div>
        </div>
        <div className="chart-wrapper" style={{ height: 280 }}>
          {monthlyTrendData.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={monthlyTrendData} isAnimationActive animationDuration={1200}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis 
                  dataKey="month" 
                  stroke="var(--muted-foreground)" 
                  tick={{ fontSize: 12 }}
                />
                <YAxis 
                  stroke="var(--muted-foreground)" 
                  tick={{ fontSize: 12 }}
                  tickFormatter={(value) => `₹${value.toLocaleString('en-IN')}`}
                />
                <Tooltip 
                  formatter={(value) => [`₹${Number(value).toLocaleString('en-IN')}`, 'Amount']}
                  labelFormatter={(label) => {
                    const item = monthlyTrendData.find(d => d.month === label);
                    return item?.fullMonth || label;
                  }}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="amount"
                  stroke="#6366f1"
                  strokeWidth={3}
                  dot={{ fill: '#6366f1', r: 5 }}
                  activeDot={{ r: 7 }}
                  name="Monthly Spending"
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="chart-placeholder">Add expenses to see monthly trends</div>
          )}
        </div>
      </motion.div>

      {/* Weekly Comparison Chart */}
      <motion.div 
        className="card card--chart" 
        initial="hidden"
        animate="show"
        variants={{ hidden: { opacity: 0, y: 14 }, show: { opacity: 1, y: 0, transition: { delay: 0.5 } } }}
      >
        <div className="card__header">
          <div>
            <h2 className="card__title">Weekly Comparison</h2>
            <p className="card__subtitle">Last 4 weeks spending</p>
          </div>
        </div>
        <div className="chart-wrapper" style={{ height: 200 }}>
          {weeklyComparisonData.some(w => w.amount > 0) ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={weeklyComparisonData} isAnimationActive animationDuration={1000}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis 
                  dataKey="week" 
                  stroke="var(--muted-foreground)" 
                  tick={{ fontSize: 11 }}
                />
                <YAxis 
                  stroke="var(--muted-foreground)" 
                  tick={{ fontSize: 11 }}
                  tickFormatter={(value) => `₹${value}`}
                />
                <Tooltip 
                  formatter={(value) => [`₹${Number(value).toLocaleString('en-IN')}`, 'Amount']}
                  labelFormatter={(label, payload) => {
                    const item = payload?.[0]?.payload;
                    return item ? `${label}: ${item.start} - ${item.end}` : label;
                  }}
                />
                <Bar 
                  dataKey="amount" 
                  fill="#22c55e" 
                  radius={[8, 8, 0, 0]}
                  animationBegin={0}
                />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="chart-placeholder">Add expenses to see weekly comparison</div>
          )}
        </div>
      </motion.div>

      {/* Category Distribution Pie Chart */}
      <motion.div 
        className="card card--chart" 
        initial="hidden"
        animate="show"
        variants={{ hidden: { opacity: 0, y: 14 }, show: { opacity: 1, y: 0, transition: { delay: 0.6 } } }}
      >
        <div className="card__header">
          <div>
            <h2 className="card__title">Category Breakdown</h2>
            <p className="card__subtitle">Where your money goes</p>
          </div>
        </div>
        <div className="chart-wrapper chart-wrapper--pie">
          {pieData.length > 0 ? (
            <>
              <div style={{ height: 180 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData}
                      dataKey="value"
                      nameKey="name"
                      outerRadius={70}
                      innerRadius={35}
                      paddingAngle={3}
                      isAnimationActive
                      animationDuration={900}
                    >
                      {pieData.map((entry, index) => (
                        <Cell
                          key={entry.name}
                          fill={CATEGORY_COLORS[index % CATEGORY_COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value) => [`₹${Number(value).toLocaleString('en-IN')}`, 'Amount']}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="legend" aria-label="Category legend">
                {pieData.map((entry, index) => (
                  <div key={entry.name} className="legend__item" title="Category share">
                    <span
                      className="legend__dot"
                      style={{ backgroundColor: CATEGORY_COLORS[index % CATEGORY_COLORS.length] }}
                    />
                    <span className="legend__label">{entry.name}</span>
                    <span className="legend__value">
                      ₹{entry.value.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                    </span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="chart-placeholder">No categories to display</div>
          )}
        </div>
      </motion.div>
    </section>
  );

  if (!token) {
    return (
      <div className={`app-shell app-shell--auth app-shell--${theme}`}>
        <ToastHost toasts={toasts} onDismiss={(id) => setToasts((prev) => prev.filter((t) => t.id !== id))} />
        <div className="auth-card">
          <div className="auth-card__header">
            <div className="auth-logo">₹</div>
            <div>
              <h1 className="auth-title">AI Expense Tracker</h1>
              <p className="auth-subtitle">Login to continue</p>
            </div>
          </div>

          <div className="auth-toggle">
            <button
              className={`auth-toggle__btn ${authMode === 'login' ? 'auth-toggle__btn--active' : ''}`}
              type="button"
              onClick={() => setAuthMode('login')}
            >
              Login
            </button>
            <button
              className={`auth-toggle__btn ${authMode === 'register' ? 'auth-toggle__btn--active' : ''}`}
              type="button"
              onClick={() => setAuthMode('register')}
            >
              Sign up
            </button>
          </div>

          <form className="auth-form" onSubmit={handleAuthSubmit}>
            {authMode === 'register' ? (
              <div className="auth-field">
                <label>Full name</label>
                <input
                  type="text"
                  value={authForm.name}
                  onChange={(e) => setAuthForm((p) => ({ ...p, name: e.target.value }))}
                  required
                />
              </div>
            ) : null}

            <div className="auth-field">
              <label>Email</label>
              <input
                type="email"
                value={authForm.email}
                onChange={(e) => setAuthForm((p) => ({ ...p, email: e.target.value }))}
                required
              />
            </div>

            <div className="auth-field">
              <label>Password</label>
              <input
                type="password"
                value={authForm.password}
                onChange={(e) => setAuthForm((p) => ({ ...p, password: e.target.value }))}
                required
              />
            </div>

            {authError ? <div className="auth-error">{authError}</div> : null}

            <RippleButton className="primary-btn" type="submit" disabled={authLoading}>
              {authLoading ? 'Please wait…' : authMode === 'login' ? 'Login' : 'Create account'}
            </RippleButton>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className={`app-shell app-shell--${theme}`}>
      <ToastHost toasts={toasts} onDismiss={(id) => setToasts((prev) => prev.filter((t) => t.id !== id))} />

      {/* Instagram-Style Mobile Bottom Navigation */}
      <MobileBottomNav
        activePage={activePage}
        setActivePage={setActivePage}
        theme={theme}
        setTheme={setTheme}
        user={user}
        handleLogout={handleLogout}
        showAddModal={() => setIsAddModalOpen(true)}
      />

      {/* Desktop Sidebar */}
      <aside className="sidebar" aria-label="Sidebar navigation">
        <div className="sidebar__brand">
          <div className="sidebar__logo">₹</div>
          <div>
            <div className="sidebar__title">Kharcha AI</div>
            <div className="sidebar__subtitle">Expense Intelligence</div>
          </div>
        </div>

        <nav className="sidebar__nav">
          {SIDEBAR_ITEMS.map((item) => {
            const active = activePage === item.id;
            return (
              <button
                key={item.id}
                type="button"
                title={item.label}
                className={`sidebar__nav-item ${active ? 'sidebar__nav-item--active' : ''}`}
                onClick={() => setActivePage(item.id)}
              >
                {active ? <motion.span className="sidebar__indicator" layoutId="sidebarIndicator" /> : null}
                <span className="sidebar__nav-item-label">{item.label}</span>
              </button>
            );
          })}
        </nav>

        <div className="sidebar__footer">
          <ThemeToggle
            theme={theme}
            onToggle={() => setTheme((t) => (t === 'dark' ? 'light' : 'dark'))}
          />
        </div>
      </aside>

      <div className="main">
        <header className="topbar topbar--sticky">
          <div>
            <h1 className="topbar__title">
              {activePage === 'dashboard' && 'Dashboard'}
              {activePage === 'expenses' && 'Expenses'}
              {activePage === 'insights' && 'Insights'}
            </h1>
            <p className="topbar__subtitle">Track, analyze and optimize your खर्च.</p>
          </div>

          <div className="topbar__right">
            <div className="topbar__theme">
              <ThemeToggle theme={theme} onToggle={() => setTheme((t) => (t === 'dark' ? 'light' : 'dark'))} />
            </div>
            <UserMenu
              user={user}
              theme={theme}
              onThemeToggle={() => setTheme((t) => (t === 'dark' ? 'light' : 'dark'))}
              onLogout={handleLogout}
            />
          </div>
        </header>

        <main className="content">
          <AnimatePresence mode="wait">
            <motion.div
              key={activePage}
              initial={{ opacity: 0, x: 14 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -10 }}
              transition={{ duration: 0.22 }}
            >
              {activePage === 'dashboard' && dashboardCards}

              {activePage !== 'dashboard' && (
                <section className="layout-grid">
                  <div className="layout-grid__main">
                    <motion.div className="card" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
                      <div className="card__header">
                        <div>
                          <h2 className="card__title">{activePage === 'expenses' ? 'Expenses' : 'Expenses insights'}</h2>
                          <p className="card__subtitle">Premium controls with search, filters, animations.</p>
                        </div>

                        <div className="expenses-header">
                          <div className="expenses-filters">
                            <input
                              type="text"
                              placeholder="Search expenses..."
                              value={searchInput}
                              onChange={(e) => setSearchInput(e.target.value)}
                              className="filters__search"
                            />
                            <CategoryMultiSelect
                              options={availableCategories}
                              selected={selectedCategories}
                              onChange={(next) => setSelectedCategories(next)}
                            />
                            <MonthDropdown months={monthOptions} value={monthFilter} onChange={setMonthFilter} />
                          </div>
                          {activePage === 'expenses' && (
                            <div className="expenses-actions">
                              <RippleButton
                                className="primary-btn"
                                type="button"
                                onClick={() => setIsAddModalOpen(true)}
                                title="Add a new expense"
                              >
                                + Add
                              </RippleButton>
                            </div>
                          )}
                        </div>
                      </div>

                      {expensesLoading ? (
                        <div className="skeleton-table">
                          <div className="skeleton-row" />
                          <div className="skeleton-row" />
                          <div className="skeleton-row" />
                        </div>
                      ) : expensesError ? (
                        <div className="error-banner">{expensesError}</div>
                      ) : (
                        <div className="table-wrapper">
                          <motion.div
                            key={`${monthFilter}|${debouncedSearch}|${selectedCategories.join(',')}`}
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.2 }}
                          >
                            <table className="table">
                              <thead>
                                <tr>
                                  <th>Date</th>
                                  <th>Description</th>
                                  <th>Category</th>
                                  <th className="table__col-right">Amount (₹)</th>
                                  {activePage === 'expenses' ? <th /> : <th />}
                                </tr>
                              </thead>

                              <tbody>
                                {filteredExpenses.length === 0 ? (
                                  <tr>
                                    <td colSpan={5} className="table__empty">
                                      No expenses found. Add one to get insights.
                                    </td>
                                  </tr>
                                ) : (
                                  <AnimatePresence mode="popLayout">
                                    {filteredExpenses.map((e) => (
                                      <motion.tr
                                        key={e._id}
                                        layout
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -8 }}
                                        transition={{ duration: 0.18 }}
                                        className={e._id === newlyAddedId ? 'row-highlight' : ''}
                                      >
                                        <td title={new Date(e.date).toISOString()}>{new Date(e.date).toLocaleDateString()}</td>
                                        <td>{e.description}</td>
                                        <td>
                                          <span className="badge" title="Expense category">
                                            {e.category}
                                          </span>
                                        </td>
                                        <td className="table__col-right">
                                          {Number(e.amount).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                                        </td>
                                        <td className="table__col-right">
                                          {activePage === 'expenses' ? (
                                            <RippleButton
                                              className="icon-btn"
                                              type="button"
                                              onClick={() => handleDeleteExpense(e._id)}
                                              title="Delete expense"
                                            >
                                              ✕
                                            </RippleButton>
                                          ) : null}
                                        </td>
                                      </motion.tr>
                                    ))}
                                  </AnimatePresence>
                                )}
                              </tbody>
                            </table>
                          </motion.div>
                        </div>
                      )}
                    </motion.div>
                  </div>

                  <div className="layout-grid__side">
                    <motion.div className="card card--chart" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
                      <div className="card__header">
                        <div>
                          <h2 className="card__title">{activePage === 'insights' ? 'Spending trend' : 'Monthly trend'}</h2>
                          <p className="card__subtitle">Charts animate on mount with premium feel.</p>
                        </div>
                      </div>

                      {expensesLoading ? (
                        <div className="chart-placeholder">Loading chart…</div>
                      ) : lineChartData.length === 0 ? (
                        <div className="chart-placeholder">Add a few expenses to see the trend.</div>
                      ) : (
                        <motion.div
                          className="chart-wrapper"
                          initial={{ opacity: 0, y: 10, scale: 0.99 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          transition={{ duration: 0.25 }}
                        >
                          <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={lineChartData} isAnimationActive animationDuration={900}>
                              <defs>
                                <linearGradient id="colorSpend" x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="5%" stopColor="#6366f1" stopOpacity={0.8} />
                                  <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                                </linearGradient>
                              </defs>
                              <XAxis dataKey="date" stroke="var(--muted-foreground)" tick={{ fontSize: 12 }} />
                              <YAxis stroke="var(--muted-foreground)" tick={{ fontSize: 12 }} />
                              <Tooltip />
                              <Area
                                type="monotone"
                                dataKey="total"
                                stroke="#6366f1"
                                fillOpacity={1}
                                fill="url(#colorSpend)"
                              />
                            </AreaChart>
                          </ResponsiveContainer>
                        </motion.div>
                      )}
                    </motion.div>

                    <motion.div className="card card--chart" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
                      <div className="card__header">
                        <div>
                          <h2 className="card__title">Categories</h2>
                          <p className="card__subtitle">Distribution of this month.</p>
                        </div>
                      </div>

                      {expensesLoading ? (
                        <div className="chart-placeholder">Loading pie…</div>
                      ) : (
                        <div className="chart-wrapper chart-wrapper--pie">
                          {pieData.length === 0 ? (
                            <div className="chart-placeholder">No data yet.</div>
                          ) : (
                            <motion.div
                              initial={{ opacity: 0, scale: 0.99, y: 8 }}
                              animate={{ opacity: 1, scale: 1, y: 0 }}
                              transition={{ duration: 0.25 }}
                              style={{ height: 180 }}
                            >
                              <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                  <Pie
                                    data={pieData}
                                    dataKey="value"
                                    nameKey="name"
                                    outerRadius={70}
                                    innerRadius={40}
                                    paddingAngle={2}
                                    isAnimationActive animationDuration={900}
                                  >
                                    {pieData.map((entry, index) => (
                                      <Cell
                                        key={entry.name}
                                        fill={CATEGORY_COLORS[index % CATEGORY_COLORS.length]}
                                      />
                                    ))}
                                  </Pie>
                                </PieChart>
                              </ResponsiveContainer>
                            </motion.div>
                          )}

                          <div className="legend" aria-label="Category legend">
                            {pieData.map((entry, index) => (
                              <div key={entry.name} className="legend__item" title="Category share">
                                <span
                                  className="legend__dot"
                                  style={{ backgroundColor: CATEGORY_COLORS[index % CATEGORY_COLORS.length] }}
                                />
                                <span className="legend__label">{entry.name}</span>
                                <span className="legend__value">
                                  ₹{entry.value.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </motion.div>

                    <motion.div className="card card--insights" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
                      <div className="card__header">
                        <div>
                          <h2 className="card__title">Smart insights</h2>
                          <p className="card__subtitle">Animated category progress + generated notes.</p>
                        </div>
                      </div>

                      <div className="insights-list">
                        {topCategoryBars.length ? (
                          topCategoryBars.map((b, idx) => (
                            <div key={b.category} className="insight-bar">
                              <div className="insight-bar__row">
                                <span className="insight-bar__label">{b.category}</span>
                                <span className="insight-bar__value">{b.pct}%</span>
                              </div>
                              <motion.div
                                className="insight-bar__track"
                                initial={{ width: 0 }}
                                animate={{ width: `${b.pct}%` }}
                                transition={{ duration: 0.65, delay: 0.06 * idx }}
                              />
                            </div>
                          ))
                        ) : (
                          <div className="insights-list__item insights-list__item--muted">
                            Add more खर्च and we’ll surface patterns like “You spend 40% on Food” or “Your spending increased this week”.
                          </div>
                        )}

                        <div className="insights-divider" />

                        {summary?.insights?.length ? (
                          summary.insights.map((i, idx) => (
                            <div key={idx} className="insights-list__item insights-list__item--premium">
                              {i}
                            </div>
                          ))
                        ) : null}
                      </div>
                    </motion.div>
                  </div>
                </section>
              )}
            </motion.div>
          </AnimatePresence>
        </main>
      </div>

      <Modal
        open={isAddModalOpen}
        title="Add expense (AI-assisted)"
        onClose={() => {
          setIsAddModalOpen(false);
          setAiLoading(false);
        }}
        footer={
          <>
            <RippleButton className="ghost-btn" type="button" onClick={() => setIsAddModalOpen(false)}>
              Cancel
            </RippleButton>
            <RippleButton className="primary-btn" type="submit" form="add-expense-form" disabled={addLoading}>
              {addLoading ? 'Saving…' : 'Add expense'}
            </RippleButton>
          </>
        }
      >
        <form id="add-expense-form" onSubmit={handleAddExpense}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <div className="float-field">
                <input
                  className="float-field__input"
                  type="number"
                  min="0"
                  placeholder=" "
                  value={newExpenseForm.amount}
                  onChange={(e) => setNewExpenseForm((p) => ({ ...p, amount: e.target.value }))}
                  required
                />
                <label className="float-field__label">Amount (₹)</label>
              </div>
            </div>

            <div>
              <div className="float-field">
                <input
                  className="float-field__input"
                  type="date"
                  placeholder=" "
                  value={newExpenseForm.date}
                  onChange={(e) => setNewExpenseForm((p) => ({ ...p, date: e.target.value }))}
                  required
                />
                <label className="float-field__label">Date</label>
              </div>
            </div>

            <div style={{ gridColumn: 'span 2' }}>
              <div className="float-field">
                <input
                  className="float-field__input"
                  placeholder=" "
                  value={newExpenseForm.description}
                  onChange={(e) => setNewExpenseForm((p) => ({ ...p, description: e.target.value }))}
                />
                <label className="float-field__label">Description</label>
              </div>
            </div>

            <div style={{ gridColumn: 'span 2' }}>
              <div className="float-field">
                <select
                  className="float-field__input category-dropdown"
                  value={newExpenseForm.category}
                  onChange={(e) => setNewExpenseForm((p) => ({ ...p, category: e.target.value }))}
                >
                  {['Food', 'Transport', 'Shopping', 'Bills', 'Entertainment', 'Health', 'Travel', 'Other'].map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
                <label className="float-field__label">Category</label>
              </div>
            </div>
          </div>

          <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px dashed rgba(148, 163, 184, 0.45)' }}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 260 }}>
                <div className="float-field">
                  <input
                    className="float-field__input"
                    placeholder=" "
                    value={aiText}
                    onChange={(e) => setAiText(e.target.value)}
                    title="Example: Spent 300 on pizza"
                  />
                  <label className="float-field__label">AI input (natural language)</label>
                </div>
              </div>
              <RippleButton
                className="ghost-btn"
                type="button"
                onClick={handleAnalyzeText}
                disabled={aiLoading || !aiText.trim()}
                title="Parse the sentence into amount/category/description"
              >
                {aiLoading ? 'Thinking…' : 'Parse with AI'}
              </RippleButton>
            </div>
          </div>
        </form>
      </Modal>
    </div>
  );
}

